import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, PREVIEW_KEY, TOKEN_KEY } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [authing, setAuthing] = useState(false);
  const processed = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("ref_code", ref);

    // short share link: /join -> owner's referral link
    if (window.location.pathname.replace(/\/+$/, "") === "/join") {
      api.get("/join")
        .then((r) => { localStorage.setItem("ref_code", r.data.code); window.location.replace(`/?ref=${r.data.code}`); })
        .catch(() => window.location.replace("/"));
      return;
    }

    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      setAuthing(true);
      (async () => {
        try {
          const r = await api.post("/auth/session", {
            session_id: sid,
            ref: localStorage.getItem("ref_code") || null,
          });
          // keep the token locally: on a custom domain the API cookie is 3rd-party and Safari drops it
          if (r.data?.session_token) localStorage.setItem(TOKEN_KEY, r.data.session_token);
          const { session_token, ...me } = r.data;
          setUser(me);
          localStorage.removeItem("ref_code");
        } catch {
          await refresh();
        } finally {
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          setAuthing(false);
        }
      })();
    } else {
      refresh();
    }
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem(PREVIEW_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const previewFree = !!user?.is_admin && localStorage.getItem(PREVIEW_KEY) === "1";
  const setPreviewFree = (on) => {
    if (on) localStorage.setItem(PREVIEW_KEY, "1"); else localStorage.removeItem(PREVIEW_KEY);
    window.location.reload();
  };

  return (
    <AuthCtx.Provider value={{
      user, isPro: !!user?.is_pro, isAdmin: !!user?.is_admin, loading: user === undefined,
      authing, login, logout, refresh, previewFree, setPreviewFree,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}
